import React, { useState, useCallback, useEffect, useRef } from 'react';
import Notiflix from 'notiflix';
import { useTheme } from 'styled-components';

import { FormHandles } from '@unform/core';
import { useHistory, useLocation } from 'react-router-dom';
import PageWrapper from '../../components/PageWrapper';
import Table, { Column } from '../../components/Table';
import { SubTableContainer } from '../../components/Table/styles';
import api from '../../services/api/api';
import { AddField as addField } from '../../utils/tableUtils';

import Title from '../../components/Title';

import { TableContainer, Container, ButtonsWrapper } from './styles';

import { useAuth } from '../../hooks/auth';
import { useContract } from '../../hooks/contract';
import {
  AgeRangeTableRow,
  ListOfProductsDataResponse,
  ListProductsResponse,
  TablePriceRow,
} from './types';
import SelectUnform from '../../components/SelectUnform';
import FormWrapper from '../../components/FormWrapper';
import Button from '../../components/Button';
import { getYupErrors, validator } from './validators';

const asyncTry = async (
  fn: () => Promise<void>,
  onError: () => void,
  cls = 'table-loading',
) => {
  try {
    Notiflix.Block.circle(`.${cls}`);
    await fn();
  } catch (err) {
    console.log(err);
    Notiflix.Notify.failure(
      'Ops... Não conseguimos buscar os dados, tente novamente mais tarde.',
    );
    onError();
  } finally {
    Notiflix.Block.remove(`.${cls}`);
  }
};

const SubAgeRangeTable = ({
  adjustments,
}: {
  adjustments: AgeRangeTableRow['adjustments'];
}) => {
  return (
    <Table
      items={adjustments}
      className="intern-table-loading"
      columnResizeMode="fit"
    >
      <Column
        field="adjustmentDate"
        header="Data reajuste"
        className="date"
        body={(data) => addField(data.adjustmentDate, 'Data reajuste')}
      />
      <Column
        field="adjustmentIndex"
        header="Índice reajuste"
        body={(data) => addField(data.adjustmentIndex, 'Índice reajuste')}
      />
      <Column
        field="adjustmentAccrual"
        header="% reajuste"
        body={(data) => addField(`${data.adjustmentAccrual}`, '% reajuste')}
      />
      <Column
        field="baseValue"
        header="Valor base"
        className="currency"
        body={(data) => addField(data.baseValue, 'Valor base')}
      />
      <Column
        field="adjustedValue"
        header="Valor reajustado"
        className="currency"
        body={(data) => addField(data.adjustedValue, 'Valor reajustado')}
      />
      <Column
        field="liberationDate"
        header="Data liberação"
        className="date"
        body={(data) => addField(data.liberationDate, 'Data liberação')}
      />
      <Column
        field="batch"
        header="Lote reajustado"
        body={(data) => addField(data.batch, 'Lote reajustado')}
      />
      <Column
        field="initialPeriodDate"
        header="Data início aplicação"
        className="date"
        body={(data) =>
          addField(data.initialPeriodDate, 'Data início aplicação')
        }
      />
      <Column
        field="finalPeriodDate"
        header="Data fim aplicação"
        className="date"
        body={(data) => addField(data.finalPeriodDate, 'Data fim aplicação')}
      />
    </Table>
  );
};

const PriceTable: React.FC = () => {
  const { user } = useAuth();
  const { contract } = useContract();
  const { colors } = useTheme();
  const defaultColor = colors.palet.institutional;
  const { search } = useLocation();
  const history = useHistory();

  const formRef = useRef<FormHandles>(null);
  const [productsList, setProductsList] = useState<
    ListProductsResponse['content']
  >([]);
  const [priceTableData, setPriceTableData] = useState<TablePriceRow[]>([]);
  const [ageRangeTableData, setAgeRangeTableData] = useState<
    AgeRangeTableRow[]
  >([]);
  const [selectedProduct, setSelectedProduct] = useState(() =>
    String(new URLSearchParams(search).get('produto') ?? ''),
  );

  const [expandedRows, setExpandedRows] = useState([] as AgeRangeTableRow[]);

  const rowExpansionTemplate = useCallback((rowData: AgeRangeTableRow) => {
    return (
      <SubTableContainer>
        <SubAgeRangeTable adjustments={rowData.adjustments} />
      </SubTableContainer>
    );
  }, []);

  const getDataFromApi = useCallback(async () => {
    await asyncTry(
      async function execute() {
        const { webUsername } = user;
        const { contractId } = contract;
        const searchQuery = new URLSearchParams(search);
        const queryProductId = searchQuery.get('produto');
        const queryTableId = searchQuery.get('tabela');

        const productsListResponse = await api.get<ListProductsResponse>(
          `/company/list-products?contractId=${contractId}&Usernameweb=${webUsername}`,
        );
        setProductsList(productsListResponse.data.content);
        if (queryProductId) {
          // set product value based on query param
          formRef.current?.setFieldValue('product', String(queryProductId));
        }
        const tableId =
          queryTableId ??
          productsListResponse.data?.content?.find(
            (item) => String(item.planCode) === String(queryProductId),
          )?.tableCode;
        if (tableId) {
          // set tableId based on query param
          formRef.current?.setFieldValue('table', String(tableId));
        }

        const tableData = await api.get<ListOfProductsDataResponse>(
          `/company/list-data-products?contractId=${contractId}&tableId=${tableId}`,
        );

        const tableNameData = tableData.data?.content?.filter(
          (row) => 'tableName' in row,
        ) as TablePriceRow[];
        const tableAgeData = tableData.data?.content?.filter(
          (row) => !('tableName' in row),
        ) as AgeRangeTableRow[];

        setPriceTableData(tableNameData);
        setAgeRangeTableData(tableAgeData);
      },
      function onError() {
        setPriceTableData([]);
        setAgeRangeTableData([]);
      },
    );
  }, [user, contract, search]);

  const onRowToggleAgeRange = useCallback(
    ({ data }: { data: AgeRangeTableRow[] }) => {
      setExpandedRows([data[data.length - 1]]);
    },
    [],
  );

  const onRowClickAgeRange = useCallback(
    ({ data }: { data: AgeRangeTableRow }) => {
      setExpandedRows((prev) => {
        if (prev.length > 0) {
          return JSON.stringify(prev[0]) !== JSON.stringify(data) ? [data] : [];
        }
        return [data];
      });
    },
    [],
  );

  const handleOnFilter = useCallback(
    async (data) => {
      try {
        await validator(data);
        history.push({
          search: `?produto=${data.product}&tabela=${data.table}`,
        });
      } catch (err) {
        const errors = getYupErrors(err);
        if (errors) formRef.current?.setErrors(errors);
        Notiflix.Notify.info('Verifique se todos os campos estão preenchidos.');
      }
    },
    [history],
  );

  const handleOnChangeSelectedProduct = useCallback((_, id: string) => {
    setSelectedProduct(id);
    formRef.current?.setFieldValue('table', '');
  }, []);

  useEffect(() => {
    getDataFromApi();
  }, [getDataFromApi]);

  return (
    <PageWrapper gridTemplateColumns="auto 1fr">
      <Container>
        <div style={{ marginTop: '2rem' }}>
          <FormWrapper
            onSubmit={handleOnFilter}
            formLook
            ref={formRef}
            style={{ display: 'flex', flexWrap: 'wrap' }}
          >
            <SelectUnform
              name="product"
              options={(productsList ?? []).map((item) => ({
                id: String(item.planCode),
                name: item.planDescription,
              }))}
              callbackOnChange={handleOnChangeSelectedProduct}
              formLook
              title="Produto"
              widthContainerDesktop="50%"
            />
            <SelectUnform
              name="table"
              options={(productsList ?? [])
                .filter((item) => String(item.planCode) === selectedProduct)
                .map((item) => ({
                  id: String(item.tableCode),
                  name: item.tableDescription,
                }))}
              formLook
              title="Tabela de preço"
              widthContainerDesktop="50%"
            />

            <ButtonsWrapper>
              <Button formLook secondary onClick={() => history.goBack()}>
                Voltar
              </Button>
              <Button
                className="step3-button-notiflix"
                grayButton
                formLook
                type="submit"
              >
                Buscar
              </Button>
            </ButtonsWrapper>
          </FormWrapper>
        </div>
        <Title themeColor={defaultColor}>Tabela de Preço</Title>
        <TableContainer>
          <Table
            items={priceTableData}
            className="table-loading"
            columnResizeMode="fit"
            // scrollable
          >
            <Column
              field="tableName"
              header="Nome da tabela"
              sortable
              body={(data) => addField(data.tableName, 'Nome da tabela')}
            />
            <Column
              field="startValidityDate"
              header="Data início vigência"
              className="date"
              body={(data) =>
                addField(data.startValidityDate, 'Data início vigência')
              }
            />
            <Column
              field="endDate"
              header="Data fim vigência"
              className="date"
              body={(data) => addField(data.endDate, 'Data fim vigência')}
            />
            <Column
              field="liberationDate"
              header="Data liberação"
              className="date"
              body={(data) => addField(data.liberationDate, 'Data liberação')}
            />
            <Column
              field="contractDate"
              header="Data contrato"
              className="date"
              body={(data) => addField(data.contractDate, 'Data contrato')}
            />
            <Column
              field="membershipProposal"
              header="Proposta de adesão"
              className="date"
              body={(data) =>
                addField(data.membershipProposal, 'Proposta de adesão')
              }
            />
            <Column
              field="quantityLife"
              header="Qtd. de vidas"
              className="date"
              body={(data) => addField(data.quantityLife, 'Qtd. de vidas')}
            />
          </Table>
        </TableContainer>
        <Title themeColor={defaultColor}>Faixa etária</Title>
        <TableContainer>
          <Table
            items={ageRangeTableData}
            className="table-loading"
            columnResizeMode="fit"
            expandedRows={expandedRows}
            onRowToggle={onRowToggleAgeRange}
            onRowClick={onRowClickAgeRange}
            rowExpansionTemplate={rowExpansionTemplate}
            tableStyle={{ tableLayout: 'fixed', width: '100%' }}
            style={{ width: '100%' }}
            scrollable
          >
            <Column
              field="quantityInitialAge"
              header="Idade inicial"
              className="date"
              // style={{ width: 'calc(100%/6)' }}
              body={(data) =>
                addField(data.quantityInitialAge, 'Idade inicial')
              }
            />
            <Column
              field="quantityFinalAge"
              header="Idade final"
              // style={{ width: 'calc(100%/6)' }}
              body={(data) => addField(data.quantityFinalAge, 'Idade final')}
            />
            <Column
              field="startPriceValue"
              header="Preço inicial"
              //  className="currency"
              // style={{ width: 'calc(100%/6)' }}
              body={(data) =>
                addField(data.startPriceValue, 'Preço inicial', true)
              }
            />
            <Column
              field="currentPriceValue"
              header="Preço atual"
              // className="currency"
              // style={{ width: 'calc(100%/6)' }}
              body={(data) =>
                addField(data.currentPriceValue, 'Preço atual', true)
              }
            />
            <Column
              field="adaptationValue"
              header="Teto máximo"
              // className="currency"
              // style={{ width: 'calc(100%/6)' }}
              body={(data) =>
                addField(data.ceilingMaximumValue ?? 0, 'Teto máximo', true)
              }
            />
            <Column
              header="Reajustes"
              expander
              style={{ width: 'calc(100%/6)', textAlign: 'right' }}
              bodyClassName="expandable"
            />
          </Table>
        </TableContainer>
      </Container>
    </PageWrapper>
  );
};

export default PriceTable;
